import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { BaseDialog } from '@/components/common/BaseDialog';
import { ModalIndices } from '@/common/i-doc/i-doc.registry.ts';
import { ConstructionStep, CreateStepDto } from '@/types/questionnaire-construction';

interface StepFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (dto: CreateStepDto) => Promise<void>;
  editingStep: ConstructionStep | null;
  nextOrder: number;
}

function emptyForm(nextOrder: number): CreateStepDto {
  return { order: nextOrder, nestCaptionI18n: { uk: '' }, nestDescriptionI18n: { uk: '' } };
}

export const StepFormDialog: React.FC<StepFormDialogProps> = ({ open, onClose, onSave, editingStep, nextOrder }) => {
  const [form, setForm] = useState<CreateStepDto>(emptyForm(nextOrder));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingStep) {
      setForm({
        order: editingStep.order,
        nestCaptionI18n: { uk: editingStep.nestCaptionI18n?.uk ?? '' },
        nestDescriptionI18n: { uk: editingStep.nestDescriptionI18n?.uk ?? '' },
      });
    } else {
      setForm(emptyForm(nextOrder));
    }
  }, [open, editingStep, nextOrder]);

  const isValid = (form.nestCaptionI18n?.uk ?? '').trim().length > 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={editingStep ? 'Редагувати крок' : 'Новий крок'}
      maxWidth="xs"
      iDoc={ModalIndices.QUIZ_BUILDER_STEP_FORM}
      actions={
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Назва кроку"
          value={form.nestCaptionI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestCaptionI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
          autoFocus
        />
        <TextField
          label="Опис кроку"
          value={form.nestDescriptionI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestDescriptionI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
      </Box>
    </BaseDialog>
  );
};
